import React, { useState, useEffect } from "react";
import "./SubscriptionPopup.scss";
import ApiService from "./../../services/api-service";
import { getErrorString, getUserId, getUserDetail } from "./../../constants/function";
import notifier from "./../../components/Notifier";
import Loader from "./../../components/Loader";
import { useDispatch } from "react-redux";
import { getBalance } from "../../asyncAction/asyncBalance";
import { isProductionEnvironment } from "../../utils/urlUtils";

const SubscriptionPopup = ({ isOpen, onClose }) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [currentTier, setCurrentTier] = useState(null); // 'basic', 'pro', or null
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null); // 'basic' or 'pro'
  const dispatch = useDispatch();
  const userId = getUserId();

  // PayPal Plan IDs for two tiers
  const PAYPAL_PLANS = {
    basic: {
      sandbox: 'P-96L44756EL916601UNC7YG4Q',
      production: 'P-31P388067W6088546NDBAGJY'
    },
    pro: {
      sandbox: 'P-9GN64782NB692931SNEY7TNI', // Pro tier plan created in sandbox
      production: 'P-23K44038X52611303NGASEJI' // Production Pro tier plan
    }
  };

  // Subscription tier pricing and benefits
  const SUBSCRIPTION_TIERS = {
    basic: {
      price: 14.99,
      name: 'Basic',
      benefits: [
        '750 monthly streams',
        '15 song uploads per month',
        'Playlist analytics',
        'Premium badge in-app',
        '14 day cash out',
        'Less ads'
      ]
    },
    pro: {
      price: 24.99,
      name: 'Pro',
      benefits: [
        'Unlimited streams',
        'Free uploads',
        'Playlist analytics',
        'Premium badge in-app',
        'Priority submission to Siki playlists',
        'Instant withdrawal (PayPal instant-transfer fee applies)',
        'Access to PowerPlays',
        'No Ads',
        'Earn more'
      ]
    }
  };

  // Determine environment based on hostname (includes memehouse.siki.io as production)
  const isProduction = isProductionEnvironment();
  const isStaging = window.location.hostname.includes('staging');

  // Get plan ID based on selected tier
  const getPlanId = () => {
    if (!selectedTier) return null;
    return isProduction ? PAYPAL_PLANS[selectedTier].production : PAYPAL_PLANS[selectedTier].sandbox;
  };

  // Check if a tier card should be disabled (user already has that tier)
  const isTierDisabled = (tier) => {
    if (!isSubscribed) return false;
    if (tier === 'basic') return currentTier === 'basic' || !currentTier; // Legacy premium = basic
    if (tier === 'pro') return currentTier === 'pro';
    return false;
  };

  // Check subscription status when the popup opens
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (isOpen && userId) {
        // First check cached user data
        const cachedUserData = getUserDetail();
        if (cachedUserData && cachedUserData.hasPremium !== undefined) {
          setIsSubscribed(cachedUserData.hasPremium);
          setCurrentTier(cachedUserData.subscriptionTier || cachedUserData.premiumTier || null);
        }

        // Then fetch fresh data from API
        setLoading(true);
        try {
          // Get user profile to check premium status
          const response = await ApiService.USER_PROFILE();

          if (response.result) {
            let hasPremium = !!response.result.hasPremium;
            let tier = response.result.subscriptionTier || null;

            // If profile says not premium, verify with PayPal directly
            // in case the local DB is out of sync
            if (!hasPremium) {
              try {
                const verifyResult = await ApiService.VERIFY_PAYPAL_SUBSCRIPTION();
                if (verifyResult.isActive) {
                  hasPremium = true;
                  tier = verifyResult.tier || 'basic';
                }
              } catch (verifyError) {
                // PayPal verify failed - continue with profile data
                console.log('PayPal verify check:', verifyError.message);
              }
            }

            setIsSubscribed(hasPremium);
            setCurrentTier(tier);
            // Update cached data
            localStorage.setItem("userData", JSON.stringify({ userData: { ...response.result, hasPremium, subscriptionTier: tier } }));
          } else {
            setIsSubscribed(false);
            setCurrentTier(null);
          }
        } catch (error) {
          notifier.error(getErrorString(error));
        } finally {
          setLoading(false);
        }
      }
    };
    checkSubscriptionStatus();
  }, [isOpen, userId]);


  // Handle subscription with PayPal
  const handlePayPalSubscribe = async () => {
    try {
      setLoading(true);

      const planId = getPlanId();
      if (!planId) {
        notifier.error("Please select a subscription tier");
        setLoading(false);
        return;
      }

      // Create PayPal subscription using ApiService
      const response = await ApiService.CREATE_PAYPAL_SUBSCRIPTION({
        planId: planId,
        tier: selectedTier,
        returnUrl: `${window.location.origin}/app/profile?subscription=success`,
        cancelUrl: `${window.location.origin}/app/profile?subscription=cancelled`
      });

      if (response.success && response.approvalUrl) {
        notifier.info("Redirecting to PayPal...");
        // Redirect to PayPal for approval
        window.location.href = response.approvalUrl;
      } else {
        notifier.error(response.error || "Failed to create PayPal subscription");
      }
    } catch (error) {
      notifier.error("Error creating PayPal subscription: " + error.message);
    } finally {
      setLoading(false);
    }
  };


  // User has Pro tier - show "You have Premium Pro" message
  const hasProTier = isSubscribed && currentTier === 'pro';

  // User has Basic (or legacy premium with no tier) - can upgrade to Pro
  const hasBasicTier = isSubscribed && !hasProTier;

  return (
    isOpen && (
      <div className="popup-overlay">
        <div className="popup-box">
          <div className="popup-header">
            <h2>PREMIUM SUBSCRIPTION</h2>
            <button className="close-btn" onClick={onClose}>X</button>
          </div>
          {loading ? (
            <div className="loader-container">
              <Loader _class="subscription-loader" />
            </div>
          ) : (
            <>
              {/* User has Pro - fully subscribed */}
              {hasProTier && (
                <div className="subscribed-message">
                  <div className="check-icon">✓</div>
                  <h3>You have Premium Pro!</h3>
                  <p>Enjoy unlimited streaming and all exclusive features</p>
                  <div className="popup-footer">
                    <button className="subscribe-btn" onClick={onClose}>
                      Go Back
                    </button>
                  </div>
                </div>
              )}

              {/* User has Basic - show upgrade option */}
              {hasBasicTier && selectedTier === null && (
                <div className="tier-selection">
                  <h3>Upgrade Your Plan</h3>
                  <div className="tier-cards">
                    <div className="tier-card disabled">
                      <h4>{SUBSCRIPTION_TIERS.basic.name}</h4>
                      <p className="tier-price">${SUBSCRIPTION_TIERS.basic.price}/month</p>
                      <span className="current-plan-badge">Current Plan</span>
                      <ul className="tier-benefits">
                        {SUBSCRIPTION_TIERS.basic.benefits.map((benefit, idx) => (
                          <li key={idx}><span>✔️</span> {benefit}</li>
                        ))}
                      </ul>
                      <button className="select-tier-btn" disabled>Current Plan</button>
                    </div>
                    <div
                      className="tier-card pro-tier"
                      onClick={() => setSelectedTier('pro')}
                    >
                      <div className="popular-badge">UPGRADE</div>
                      <h4>{SUBSCRIPTION_TIERS.pro.name}</h4>
                      <p className="tier-price">${SUBSCRIPTION_TIERS.pro.price}/month</p>
                      <ul className="tier-benefits">
                        {SUBSCRIPTION_TIERS.pro.benefits.map((benefit, idx) => (
                          <li key={idx}><span>✔️</span> {benefit}</li>
                        ))}
                      </ul>
                      <button className="select-tier-btn pro-btn">Upgrade to Pro</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Free user - Tier Selection */}
              {!isSubscribed && selectedTier === null && (
                <>
                  <div className="tier-selection">
                    <h3>Choose Your Plan</h3>
                    <div className="tier-cards">
                      <div
                        className="tier-card"
                        onClick={() => setSelectedTier('basic')}
                      >
                        <h4>{SUBSCRIPTION_TIERS.basic.name}</h4>
                        <p className="tier-price">${SUBSCRIPTION_TIERS.basic.price}/month</p>
                        <ul className="tier-benefits">
                          {SUBSCRIPTION_TIERS.basic.benefits.map((benefit, idx) => (
                            <li key={idx}><span>✔️</span> {benefit}</li>
                          ))}
                        </ul>
                        <button className="select-tier-btn">Select Basic</button>
                      </div>
                      <div
                        className="tier-card pro-tier"
                        onClick={() => setSelectedTier('pro')}
                      >
                        <div className="popular-badge">BEST VALUE</div>
                        <h4>{SUBSCRIPTION_TIERS.pro.name}</h4>
                        <p className="tier-price">${SUBSCRIPTION_TIERS.pro.price}/month</p>
                        <ul className="tier-benefits">
                          {SUBSCRIPTION_TIERS.pro.benefits.map((benefit, idx) => (
                            <li key={idx}><span>✔️</span> {benefit}</li>
                          ))}
                        </ul>
                        <button className="select-tier-btn pro-btn">Select Pro</button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Show selected tier benefits */}
              {selectedTier && (
                <>
                  <div className="selected-tier-info">
                    <h3>{SUBSCRIPTION_TIERS[selectedTier].name} Plan</h3>
                    <ul className="popup-content">
                      {SUBSCRIPTION_TIERS[selectedTier].benefits.map((benefit, idx) => (
                        <li key={idx}><span role="img" aria-label="check">✔️</span> {benefit}</li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

              {selectedTier && (
                <>
                  <div className="plan-confirmation">
                    <h3>{SUBSCRIPTION_TIERS[selectedTier].name} Monthly Subscription</h3>
                    <p className="plan-price">${SUBSCRIPTION_TIERS[selectedTier].price}/month</p>
                    <p className="plan-note">Cancel anytime • Auto-renewal</p>
                  </div>
                  <div className="popup-footer">
                    <button className="back-btn" onClick={() => setSelectedTier(null)}>
                      ← Choose Different Plan
                    </button>
                    <button
                      className="subscribe-btn paypal"
                      onClick={handlePayPalSubscribe}
                    >
                      {hasBasicTier ? 'Upgrade with PayPal' : 'Subscribe with PayPal'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    )
  );
};

export default SubscriptionPopup;
